import Phaser from 'phaser';
import { brandHex } from '../brand';
import { HUD_AVATAR_KEY } from './avatar';

export interface HudHandles {
  setName(name: string): void;
  setHealth(segments: number, max: number): void;
  setCountdown(seconds: number, opts?: { warn?: boolean }): void;
  setCountdownLabel(label: string): void;
  setAssets(remaining: number, total: number): void;
  setAssetsLabel(label: string): void;
  flashAlert(text: string, color?: number, durationMs?: number): void;
  /** Mid-screen capsule. `durationMs = 0` keeps the banner up until
   *  hideCenterBanner() is called — used for the GRAB IONIX POWERUP cue. */
  showCenterBanner(text: string, color?: number, durationMs?: number): void;
  hideCenterBanner(): void;
  destroy(): void;
}

const $ = (id: string) => document.getElementById(id)!;

// Cached chip elements shared with the module-level updateAssetChip helper.
// Refreshed in createHud() so a Replay (new HUD instance) repoints at the
// freshly rebuilt chip nodes.
const activeChipsRef: { list: HTMLElement[] } = { list: [] };

// Map SPRITE_KEYS.asset keys to their public PNG paths for chip images
const ASSET_IMG: Record<string, string> = {
  webserver: '/sprites/asset_webserver.png',
  domain:    '/sprites/asset_domain.png',
  s3:        '/sprites/asset_s3.png',
  api:       '/sprites/asset_api.png',
  database:  '/sprites/asset_database.png',
  cloud:     '/sprites/asset_cloud.png',
  cert:      '/sprites/asset_cert.png',
  subdomain: '/sprites/asset_subdomain.png',
  email:     '/sprites/asset_email.png',
  dns:       '/sprites/asset_dns.png',
  dataleak:  '/sprites/asset_dataleak.png',
};
export const createHud = (
  scene: Phaser.Scene,
  params: { name: string; total: number; healthMax: number },
): HudHandles => {
  const overlay = $('hud-overlay');
  overlay.classList.remove('hidden');

  // --- Avatar ---
  const avatarEl = $('hud-avatar') as HTMLImageElement;
  try {
    const tex = scene.textures.get(HUD_AVATAR_KEY);
    const src = tex.getSourceImage() as HTMLCanvasElement | HTMLImageElement;
    if (src instanceof HTMLCanvasElement) avatarEl.src = src.toDataURL();
    else if (src instanceof HTMLImageElement && src.src) avatarEl.src = src.src;
  } catch { /* keep default */ }

  // --- Name ---
  $('hud-name').textContent = params.name || 'Agent';

  // --- Health segments — built once, mutated in place. setHealth() used to
  // wipe + recreate 5 divs on every CVE hit; now we just toggle classes.
  const healthContainer = $('hud-health-segs');
  const healthValueEl = $('hud-health-value');
  const buildHealthSegs = (max: number) => {
    healthContainer.innerHTML = '';
    for (let i = 0; i < max; i++) {
      const div = document.createElement('div');
      div.className = 'hud-seg';
      healthContainer.appendChild(div);
    }
  };
  const updateHealthSegs = (segments: number, max: number) => {
    const segs = healthContainer.children;
    for (let i = 0; i < max; i++) {
      const filled = i < segments;
      const danger = segments <= 2 && filled;
      const cls = 'hud-seg' + (!filled ? ' empty' : danger ? ' danger' : '');
      const el = segs[i] as HTMLElement | undefined;
      if (el && el.className !== cls) el.className = cls;
    }
    const text = `${segments}/${max}`;
    if (healthValueEl.textContent !== text) healthValueEl.textContent = text;
  };
  buildHealthSegs(params.healthMax);
  updateHealthSegs(params.healthMax, params.healthMax);

  // --- Asset chips (built once, updated in place) -----------------------
  // Chips are created here and only updated via updateAssetChip(). Don't
  // rebuild on every setAssets() call — that wipes the icons of previously
  // patched assets. The cached array also lets updateAssetChip skip a
  // querySelectorAll on every call (was N per snippet hit in boss phase).
  const chipContainer = $('hud-asset-segs');
  const initAssetChips = (total: number) => {
    chipContainer.innerHTML = '';
    for (let i = 0; i < total; i++) {
      const div = document.createElement('div');
      div.className = 'hud-asset-chip';
      chipContainer.appendChild(div);
    }
    const totalSpan = $('hud-assets-total');
    if (totalSpan) totalSpan.textContent = `/${total}`;
    cachedChips = Array.from(chipContainer.children) as HTMLElement[];
    activeChipsRef.list = cachedChips;
  };
  // Boss/Game both call updateAssetChip — without a shared cache they each
  // querySelectorAll the chip row. activeChipsRef is a tiny module-shared
  // handle the global helper at the bottom reads from.
  let cachedChips: HTMLElement[] = [];
  initAssetChips(params.total);

  const setAssetCount = (remaining: number, total: number) => {
    const valueEl = $('hud-assets-value');
    const exposed = total - remaining; // patched count
    valueEl.firstChild!.textContent = String(exposed);
  };
  setAssetCount(params.total, params.total);

  // --- Alert ---
  let alertTimer: ReturnType<typeof setTimeout> | null = null;
  const alertEl = $('hud-alert');
  let bannerTimer: ReturnType<typeof setTimeout> | null = null;
  const bannerEl = $('hud-center-banner');
  // --- Countdown memo (avoid re-formatting every frame) ---
  const countdownEl = $('hud-countdown');
  let lastCountdownSec = -1;
  let lastCountdownWarn = false;

  return {
    setName(name) {
      $('hud-name').textContent = name;
    },

    setHealth(segments, max) {
      updateHealthSegs(segments, max);
      const label = $('hud-health-label');
      if (segments <= 2) label.classList.add('danger');
      else label.classList.remove('danger');
    },

    setCountdown(seconds, opts) {
      // Called every frame by both scenes' update(); the displayed string
      // only changes once a second. Skip the format + DOM write otherwise.
      const intSec = Math.floor(seconds);
      if (intSec !== lastCountdownSec) {
        lastCountdownSec = intSec;
        const m = Math.floor(intSec / 60);
        const s = intSec % 60;
        countdownEl.textContent = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
      }
      const warn = !!(opts?.warn) || seconds <= 10;
      if (warn !== lastCountdownWarn) {
        lastCountdownWarn = warn;
        countdownEl.classList.toggle('warn', warn);
      }
    },

    setCountdownLabel(label) {
      $('hud-countdown-label').textContent = label;
    },

    setAssets(remaining, total) {
      setAssetCount(remaining, total);
    },

    setAssetsLabel(label) {
      $('hud-assets-label').textContent = label;
    },

    flashAlert(text, color = brandHex.threat, durationMs = 1200) {
      alertEl.textContent = text;
      alertEl.style.background = `#${color.toString(16).padStart(6, '0')}`;
      alertEl.style.color = '#fff';
      alertEl.classList.remove('hud-alert-hidden');
      // restart animation
      alertEl.style.animation = 'none';
      void alertEl.offsetHeight; // reflow
      alertEl.style.animation = '';
      if (alertTimer) clearTimeout(alertTimer);
      alertTimer = setTimeout(() => alertEl.classList.add('hud-alert-hidden'), durationMs);
    },

    showCenterBanner(text, color = brandHex.primary, durationMs = 1500) {
      bannerEl.textContent = text;
      bannerEl.style.background = `#${color.toString(16).padStart(6, '0')}`;
      bannerEl.classList.remove('hud-center-banner-hidden');
      bannerEl.style.animation = 'none';
      void bannerEl.offsetHeight;
      bannerEl.style.animation = '';
      if (bannerTimer) clearTimeout(bannerTimer);
      bannerTimer = null;
      // durationMs === 0 → persistent until hideCenterBanner() is called.
      if (durationMs > 0) {
        bannerTimer = setTimeout(() => bannerEl.classList.add('hud-center-banner-hidden'), durationMs);
      }
    },

    hideCenterBanner() {
      if (bannerTimer) clearTimeout(bannerTimer);
      bannerTimer = null;
      bannerEl.classList.add('hud-center-banner-hidden');
    },

    destroy() {
      if (alertTimer) clearTimeout(alertTimer);
      if (bannerTimer) clearTimeout(bannerTimer);
      overlay.classList.add('hidden');
      alertEl.classList.add('hud-alert-hidden');
      bannerEl.classList.add('hud-center-banner-hidden');
    },
  };
};

// Expose a helper for scenes that need to show an asset chip with the actual sprite.
// GameScene calls this after a scan to mark a chip patched. BossScene calls it
// once during create() to seed each chip with an asset icon, then again per
// target when its exploited state flips.
export const updateAssetChip = (
  index: number,
  kind: string,
  state: 'unknown' | 'exposed' | 'patched' | 'exploited',
) => {
  // Cached array refreshed by createHud() — avoids one querySelectorAll per
  // call (and refreshExploitedCount loops this once per boss target on
  // every snippet impact, so the cost compounded).
  const chip = activeChipsRef.list[index];
  if (!chip) return;
  const stateClass =
    state === 'exposed' ? ' exposed' :
    state === 'patched' ? ' patched' :
    state === 'exploited' ? ' exploited' : '';
  const newClass = 'hud-asset-chip' + stateClass;
  if (chip.className !== newClass) chip.className = newClass;
  if (kind && ASSET_IMG[kind] && !chip.firstChild) {
    const img = document.createElement('img');
    img.src = ASSET_IMG[kind];
    img.width = 20;
    img.height = 20;
    img.style.imageRendering = 'pixelated';
    chip.appendChild(img);
  }
};
